import React from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { DateTimePicker, FormSwitch } from '../UI';
import type { ChartSettings } from '@/components/Chart/ChartContainer.tsx';

const CHART_INTRADAY_DURATIONS = new Set(['1m', '5m', '15m', '30m', '60m', '120m', '180m', '240m']);
const QUICK_RANGE_PRESETS = ['1M', '3M', '6M', '1Y', 'YTD', 'Max'] as const;

type QuickRangePreset = (typeof QUICK_RANGE_PRESETS)[number];

const parseChartDateTime = (dateTime: string | null | undefined): Dayjs | null => {
    if (!dateTime) {
        return null;
    }

    const normalizedDateTime = dateTime.includes('T') ? dateTime : dateTime.replace(' ', 'T');
    const parsedDate = dayjs(normalizedDateTime);

    return parsedDate.isValid() ? parsedDate : null;
};

const formatChartDateTime = (dateTime: Dayjs): string => dateTime.format('YYYY-MM-DD HH:mm:ss');

const getMaxPresetStart = (chartSettings: ChartSettings): Dayjs | null => {
    const { duration, entity } = chartSettings;
    const shouldUseIntradayOldestBar = CHART_INTRADAY_DURATIONS.has(duration);

    const oldestDateCandidates = shouldUseIntradayOldestBar
        ? [entity.oldest1mBarDateTime, entity.oldestEodBarDateTime, entity.historicalTaOldestDate, 'ipo' in entity ? entity.ipo : null]
        : [entity.oldestEodBarDateTime, entity.oldest1mBarDateTime, entity.historicalTaOldestDate, 'ipo' in entity ? entity.ipo : null];

    for (const candidate of oldestDateCandidates) {
        const parsedCandidate = parseChartDateTime(candidate);

        if (parsedCandidate) {
            return candidate?.includes(' ') ? parsedCandidate : parsedCandidate.startOf('day');
        }
    }

    return null;
};

const resolveQuickRange = (
    preset: QuickRangePreset,
    chartSettings: ChartSettings
): { endDateTime: string; startDateTime: string } | null => {
    const endDateTime = parseChartDateTime(chartSettings.endDateTime) ?? dayjs().second(0);
    let startDateTime: Dayjs | null = null;

    switch (preset) {
        case '1M':
            startDateTime = endDateTime.subtract(1, 'month');
            break;
        case '3M':
            startDateTime = endDateTime.subtract(3, 'month');
            break;
        case '6M':
            startDateTime = endDateTime.subtract(6, 'month');
            break;
        case '1Y':
            startDateTime = endDateTime.subtract(1, 'year');
            break;
        case 'YTD':
            startDateTime = endDateTime.startOf('year');
            break;
        case 'Max':
            startDateTime = getMaxPresetStart(chartSettings);
            break;
    }

    if (!startDateTime) {
        return null;
    }

    if (startDateTime.isAfter(endDateTime)) {
        startDateTime = endDateTime.startOf('day');
    }

    return {
        startDateTime: formatChartDateTime(startDateTime),
        endDateTime: formatChartDateTime(endDateTime)
    };
};

interface ChartControlsProps {
    chartSettings: ChartSettings;
    className?: string;
    onAfterHoursToggle: (include: boolean) => void;
    onAutoRefreshToggle: (include: boolean) => void;
    onDateRangeChange: (start?: string, end?: string) => void;
}

const ChartControls: React.FC<ChartControlsProps> = ({
    chartSettings,
    className = '',
    onAfterHoursToggle,
    onAutoRefreshToggle,
    onDateRangeChange
}) => {
    const { startDateTime, endDateTime, entity, shouldRemoveAfterHours, shouldAutoRefreshBars } = chartSettings;
    const { entityType } = entity;

    const quickPresetRanges = QUICK_RANGE_PRESETS.map((preset) => ({
        preset,
        range: resolveQuickRange(preset, chartSettings)
    }));

    return (
        <div className={className}>
            {/* Date Range */}
            <div className="mb-4">
                <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Date Range</div>
                <DateTimePicker
                    autoApply={false}
                    className="chart-date-picker"
                    endDateTime={endDateTime}
                    startDateTime={startDateTime}
                    onDateRangeChange={(start, end) => onDateRangeChange(start, end)}
                />
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">Quick Ranges</span>
                    {quickPresetRanges.map(({ preset, range }) => (
                        <button
                            className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                                range
                                    ? 'border-zinc-700/60 bg-zinc-900/80 text-zinc-300 hover:border-zinc-500 hover:text-white'
                                    : 'cursor-not-allowed border-zinc-800 bg-zinc-900/40 text-zinc-600'
                            }`}
                            disabled={!range}
                            key={preset}
                            type="button"
                            onClick={() => {
                                if (!range) {
                                    return;
                                }

                                onDateRangeChange(range.startDateTime, range.endDateTime);
                            }}
                        >
                            {preset}
                        </button>
                    ))}
                </div>
            </div>

            {/* Data Options */}
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Options</div>
            <div className="flex flex-wrap gap-x-6 gap-y-1">
                {entityType !== 'Cryptocurrency' && (
                    <FormSwitch
                        checked={!shouldRemoveAfterHours}
                        className="mb-0"
                        id="after-hours-switch"
                        label="Extended hours"
                        onChange={(e) => onAfterHoursToggle(e.target.checked)}
                    />
                )}
                <FormSwitch
                    checked={shouldAutoRefreshBars}
                    className="mb-0"
                    id="auto-refresh-switch"
                    label="Auto Refresh"
                    onChange={(e) => onAutoRefreshToggle(e.target.checked)}
                />
            </div>
        </div>
    );
};

export default ChartControls;
